import { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Box,
    Stack,
    Typography,
    Button,
    IconButton,
    Chip,
    CircularProgress,
    InputBase,
    Divider,
    Checkbox,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    useTheme,
} from "@mui/material";
import { FoodsTooltip } from "@foodpilot/foods";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { type Ssq } from "src/api/sure-api/ssq/useGetOneSsq";
import { type SsqVersionAssignment } from "src/api/client-api/questionnaire/useGetAllSsqVersionAssignments";
import {
    type SsqPostVersionComparison,
    useGetCampaignSsqPostVersionComparison,
} from "src/api/client-api/questionnaire/useGetCampaignSsqPostVersionComparison";
import { formatDate, formatVersion } from "src/utils/formatting";
import { HTMLBox } from "src/components/HTMLBox/HTMLBox";

type ChangeVersionDialogProps = {
    onClose: () => void;
    onApply: (newVersionId: number) => void;
    isApplying: boolean;
    ssq: Ssq;
    assignment?: SsqVersionAssignment;
};

type SsqVersionChanges = SsqPostVersionComparison["versionChanges"];
type MergedSsqVersionChanges = {
    new: string[];
    deleted: string[];
    versionChanged: SsqVersionChanges["versionChanged"];
};
type SsqVersionChangeItem = {
    key: string;
    content: string;
};

const mergeSsqVersionChanges = (versionChanges: SsqVersionChanges[]): MergedSsqVersionChanges => {
    const merged: MergedSsqVersionChanges = {
        new: [],
        deleted: [],
        versionChanged: [],
    };

    const newCodes = new Set<string>();
    const deletedCodes = new Set<string>();
    const changedVersions = new Set<string>();

    versionChanges.forEach((changes) => {
        changes.new.forEach((code) => {
            if (newCodes.has(code)) return;

            newCodes.add(code);
            merged.new.push(code);
        });

        Object.values(changes.deleted).forEach((code) => {
            if (deletedCodes.has(code)) return;

            deletedCodes.add(code);
            merged.deleted.push(code);
        });

        changes.versionChanged.forEach((change) => {
            const key = `${change.code}-${change.version}`;
            if (changedVersions.has(key)) return;

            changedVersions.add(key);
            merged.versionChanged.push(change);
        });
    });

    return merged;
};

const getSsqVersionChangeItems = (versionChanges: MergedSsqVersionChanges, t: TFunction): SsqVersionChangeItem[] => [
    ...versionChanges.deleted.map((code) => ({
        key: `deleted-${code}`,
        content: t(
            "settings.questionnaires.ssq_version_change.deleted",
            "Property of code <strong>{{code}}</strong> was deleted.",
            { code },
        ),
    })),
    ...versionChanges.new.map((code) => ({
        key: `new-${code}`,
        content: t(
            "settings.questionnaires.ssq_version_change.new",
            "Property of code <strong>{{code}}</strong> has been added.",
            { code },
        ),
    })),
    ...versionChanges.versionChanged.map((change) => ({
        key: `version-changed-${change.code}-${change.version}`,
        content: t(
            "settings.questionnaires.ssq_version_change.version_changed",
            "Property <strong>{{code}}</strong> was upgraded to version <strong>{{version}}</strong>.",
            { code: change.code, version: change.version },
        ),
    })),
];

export const ChangeVersionDialog = ({ onClose, onApply, isApplying, ssq, assignment }: ChangeVersionDialogProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const ssqPostVersionComparison = useGetCampaignSsqPostVersionComparison();

    const [step, setStep] = useState<"pick" | "confirm">("pick");
    const [search, setSearch] = useState("");
    const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
    const [confirmed, setConfirmed] = useState(false);
    const [comparisonPage, setComparisonPage] = useState(0);
    const [comparisonRowsPerPage, setComparisonRowsPerPage] = useState(10);

    const currentVersionId = assignment?.ssqVersionId;
    const currentVersion =
        currentVersionId !== undefined ? ssq.versions.find((v) => v.id === currentVersionId) : undefined;
    const selectedVersion = selectedVersionId !== null ? ssq.versions.find((v) => v.id === selectedVersionId) : null;

    const publishedVersions = ssq.versions.filter((v) => v.status === 1 || v.status === 2);

    const sortedVersions = [...publishedVersions].sort((a, b) => {
        if (a.versionMajor !== b.versionMajor) return b.versionMajor - a.versionMajor;
        return b.versionMinor - a.versionMinor;
    });

    const filteredVersions = sortedVersions.filter((v) =>
        `v${v.versionMajor}.${v.versionMinor}`.includes(search.toLowerCase()),
    );

    const relatedActionPlansCount = assignment?.relatedActionPlansCount ?? 0;
    const hasRelatedActionPlans = relatedActionPlansCount > 0;
    const hasImpact = (assignment?.answerCount ?? 0) > 0 || hasRelatedActionPlans;
    const canApply = selectedVersionId !== null && selectedVersionId !== currentVersionId;
    const isLoadingComparison = ssqPostVersionComparison.isLoading;

    const comparisonRows = ssqPostVersionComparison.data?.comparisons ?? [];
    const comparisonTotalItems = ssqPostVersionComparison.data?.totalItems ?? comparisonRows.length;
    const versionChanges = mergeSsqVersionChanges(comparisonRows.map((comparison) => comparison.versionChanges));
    const versionChangeItems = getSsqVersionChangeItems(versionChanges, t);

    const handleVersionSelect = (versionId: number) => {
        setSelectedVersionId(versionId);
        setConfirmed(false);
        setComparisonPage(0);
        ssqPostVersionComparison.reset();
    };

    const handleNext = async () => {
        if (!canApply) return;
        if (hasImpact) {
            if (assignment?.campaign.id === undefined || currentVersionId === undefined) return;
            try {
                await ssqPostVersionComparison.mutateAsync({
                    campaignId: assignment.campaign.id,
                    ssqVersionId: selectedVersionId,
                    sourceSsqVersionId: currentVersionId,
                    page: 1,
                    itemsPerPage: comparisonRowsPerPage,
                });
                setComparisonPage(0);
                setStep("confirm");
            } catch {
                return;
            }
        } else {
            onApply(selectedVersionId);
        }
    };

    const borderColor = theme.custom.grey[500];

    const statusLabel = (status: number) => {
        const labels: Record<number, string> = {
            0: t("settings.questionnaires.status_draft", "Draft"),
            1: t("settings.questionnaires.status_published", "Published"),
            2: t("settings.questionnaires.status_archived", "Archived"),
        };
        return labels[status] ?? "";
    };

    const labelSx = {
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: "text.secondary",
        display: "block",
    } as const;

    const releaseNotes = selectedVersion?.releaseNotes?.trim();
    const releaseNotesBlock = selectedVersion && (
        <Box px={2} py={1.5} sx={{ bgcolor: "#fafaf9", borderRadius: "10px", border: `1px solid ${borderColor}` }}>
            <Typography variant="caption" sx={labelSx}>
                {t("settings.questionnaires.release_notes", "Release notes")} · {formatVersion(selectedVersion)}
            </Typography>
            {releaseNotes ?
                <HTMLBox value={releaseNotes} seeMoreColor="#398BDB" />
            :   <Typography variant="body" color="text.primary" sx={{ lineHeight: 1.65 }}>
                    {t("settings.questionnaires.no_release_notes", "No release notes available.")}
                </Typography>
            }
        </Box>
    );

    const versionSummary = (
        <Box px={2} py={1.5} sx={{ bgcolor: "#fafaf9", borderRadius: "10px", border: `1px solid ${borderColor}` }}>
            <Stack direction="row" alignItems="center" gap={1.5}>
                <Box>
                    <Typography variant="caption" sx={labelSx}>
                        {step === "pick" ?
                            t("settings.questionnaires.current_version", "Current version")
                        :   t("settings.questionnaires.from", "From")}
                    </Typography>
                    <Typography variant="body-bold">{currentVersion ? formatVersion(currentVersion) : "—"}</Typography>
                </Box>
                <ArrowForwardIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                <Box>
                    <Typography variant="caption" sx={labelSx}>
                        {step === "pick" ?
                            t("settings.questionnaires.new_version", "New version")
                        :   t("settings.questionnaires.to", "To")}
                    </Typography>
                    <Typography variant="body-bold" sx={{ color: selectedVersion ? "primary.main" : "text.disabled" }}>
                        {selectedVersion ?
                            formatVersion(selectedVersion)
                        :   t("settings.questionnaires.select_one", "— select one")}
                    </Typography>
                </Box>
                {step === "confirm" && (
                    <Box ml="auto">
                        <Typography variant="caption" sx={labelSx}>
                            {t("settings.questionnaires.campaign_label", "Campaign")}
                        </Typography>
                        <Typography variant="body-bold">{assignment?.campaign.label}</Typography>
                    </Box>
                )}
            </Stack>
        </Box>
    );

    return (
        <Dialog
            open
            onClose={isApplying ? undefined : onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                elevation: 8,
                sx: { borderRadius: "20px", overflow: "hidden", maxHeight: "90vh" },
            }}
        >
            {step === "pick" ?
                <>
                    <DialogTitle sx={{ pb: 0, pt: 2.5, px: 3 }}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                            <Box>
                                <Typography variant="h3" sx={{ fontWeight: 700, fontSize: 17 }}>
                                    {t("settings.questionnaires.change_version_title", "Change version")}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.4, display: "block" }}>
                                    {assignment?.campaign.label} · {ssq.title}
                                </Typography>
                            </Box>
                            <IconButton size="small" onClick={onClose} disabled={isApplying} sx={{ mt: -0.5 }}>
                                <CloseIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Stack>
                        <Box mt={2} mb={0.5}>
                            {versionSummary}
                        </Box>
                        {releaseNotesBlock && <Box mb={0.5}>{releaseNotesBlock}</Box>}
                    </DialogTitle>

                    <DialogContent sx={{ px: 3, pt: 2 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                px: 1.5,
                                py: 1,
                                borderRadius: "10px",
                                border: `1px solid ${borderColor}`,
                                bgcolor: "white",
                                mb: 1.5,
                            }}
                        >
                            <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            <InputBase
                                placeholder={t("settings.questionnaires.search_version", "Search a version...")}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                sx={{ fontSize: 13, flex: 1 }}
                            />
                        </Box>

                        <Stack gap={0.5} sx={{ maxHeight: 350, overflowY: "auto" }}>
                            {filteredVersions.map((version) => {
                                const isCurrent = version.id === currentVersionId;
                                const isArchived = version.status === 2;
                                const isDisabled = isCurrent || isArchived;
                                const isLatest = version.id === publishedVersions[0]?.id;
                                const isSelected = version.id === selectedVersionId;
                                const dateStr = version.updatedAt ?? version.createdAt;

                                return (
                                    <Box
                                        key={version.id}
                                        onClick={() => !isDisabled && handleVersionSelect(version.id)}
                                        sx={{
                                            px: 2,
                                            py: 1.5,
                                            borderRadius: "10px",
                                            border: `1.5px solid ${isSelected ? theme.palette.primary.main : borderColor}`,
                                            bgcolor:
                                                isSelected ? "rgba(1, 164, 173, 0.05)"
                                                : isDisabled ? "#fafaf9"
                                                : "white",
                                            cursor: isDisabled ? "not-allowed" : "pointer",
                                            opacity: isDisabled ? 0.6 : 1,
                                            transition: "all 0.12s",
                                            "&:hover":
                                                !isDisabled ?
                                                    {
                                                        borderColor: theme.palette.primary.main,
                                                        bgcolor: "rgba(1, 164, 173, 0.05)",
                                                    }
                                                :   {},
                                        }}
                                    >
                                        <Stack direction="row" alignItems="flex-start" gap={1.5}>
                                            <Box
                                                sx={{
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: "50%",
                                                    flexShrink: 0,
                                                    mt: 0.25,
                                                    border: `2px solid ${isSelected ? theme.palette.primary.main : borderColor}`,
                                                    bgcolor: isSelected ? theme.palette.primary.main : "transparent",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                {isSelected && (
                                                    <Box
                                                        sx={{
                                                            width: 5,
                                                            height: 5,
                                                            borderRadius: "50%",
                                                            bgcolor: "white",
                                                        }}
                                                    />
                                                )}
                                            </Box>

                                            <Box flex={1} minWidth={0}>
                                                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                                    <Typography
                                                        variant="body-bold"
                                                        color={isCurrent ? "text.secondary" : "text.primary"}
                                                    >
                                                        {formatVersion(version)}
                                                    </Typography>
                                                    {isCurrent && (
                                                        <Chip
                                                            label={t("settings.questionnaires.current", "Current")}
                                                            size="small"
                                                            sx={{
                                                                height: 18,
                                                                fontWeight: 700,
                                                                fontSize: "0.6rem",
                                                                bgcolor: "#e8f0fe",
                                                                color: "#3b5ec6",
                                                                borderRadius: "999px",
                                                            }}
                                                        />
                                                    )}
                                                    {isLatest && !isCurrent && (
                                                        <Chip
                                                            label={t(
                                                                "settings.questionnaires.recommended",
                                                                "Recommended",
                                                            )}
                                                            size="small"
                                                            sx={{
                                                                height: 18,
                                                                fontWeight: 700,
                                                                fontSize: "0.6rem",
                                                                bgcolor: "#dfffe6",
                                                                color: "#0d8728",
                                                                borderRadius: "999px",
                                                            }}
                                                        />
                                                    )}
                                                    <Chip
                                                        label={statusLabel(version.status)}
                                                        size="small"
                                                        sx={{
                                                            height: 18,
                                                            fontWeight: 600,
                                                            fontSize: "0.6rem",
                                                            borderRadius: "999px",
                                                            bgcolor: "#efedec",
                                                            color: "text.secondary",
                                                        }}
                                                    />
                                                </Stack>
                                                {dateStr && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ mt: 0.25 }}
                                                    >
                                                        {formatDate(dateStr)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                    </Box>
                                );
                            })}

                            {filteredVersions.length === 0 && (
                                <Box py={4} textAlign="center">
                                    <Typography variant="body" color="text.secondary">
                                        {t("settings.questionnaires.no_matching_version", "No matching version found.")}
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                        {ssqPostVersionComparison.isError && (
                            <Alert severity="error" sx={{ mt: 1.5 }}>
                                {t(
                                    "settings.questionnaires.version_comparison_error",
                                    "Could not calculate the migration impact. Please try again.",
                                )}
                            </Alert>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${borderColor}`, gap: 1 }}>
                        <Button
                            onClick={onClose}
                            disabled={isApplying}
                            sx={{ fontWeight: 600, color: "text.secondary", textTransform: "none" }}
                        >
                            {t("common.cancel", "Cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            disabled={!canApply || isLoadingComparison || isApplying}
                            onClick={handleNext}
                            startIcon={
                                isLoadingComparison || isApplying ?
                                    <CircularProgress size={16} color="inherit" />
                                :   undefined
                            }
                            sx={{
                                fontWeight: 700,
                                textTransform: "none",
                                borderRadius: "999px",
                                px: 3,
                                boxShadow: "none",
                                "&:hover": { boxShadow: "none" },
                            }}
                        >
                            {isLoadingComparison ?
                                t("settings.questionnaires.calculating_migration", "Calculating migration...")
                            : hasImpact && canApply ?
                                t("settings.questionnaires.continue", "Continue")
                            :   t("settings.questionnaires.apply_version", "Apply version")}
                        </Button>
                    </DialogActions>
                </>
            :   <>
                    <DialogTitle sx={{ pb: 0, pt: 2.5, px: 3 }}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                            <Box>
                                <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                                    <WarningAmberIcon sx={{ fontSize: 18, color: "warning.main" }} />
                                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: 17 }}>
                                        {t("settings.questionnaires.migration_impact", "Migration impact")}
                                    </Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                    {t(
                                        "settings.questionnaires.review_migration_impact",
                                        "Review how existing answers will move to the selected version.",
                                    )}
                                </Typography>
                            </Box>
                            <IconButton size="small" onClick={onClose} disabled={isApplying} sx={{ mt: -0.5 }}>
                                <CloseIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Stack>
                    </DialogTitle>

                    <DialogContent sx={{ px: 3, pt: 2 }}>
                        <Box mb={2}>{versionSummary}</Box>
                        {releaseNotesBlock && <Box mb={2}>{releaseNotesBlock}</Box>}
                        {hasRelatedActionPlans && (
                            <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
                                {t(
                                    "settings.questionnaires.action_plans_stale_alert",
                                    "{{actionPlanCount}} action plans will be marked as stale and will require action on them.",
                                    { actionPlanCount: relatedActionPlansCount },
                                )}
                            </Alert>
                        )}

                        <Box
                            px={2.5}
                            py={2}
                            mb={2}
                            sx={{ bgcolor: "#fff8e1", borderRadius: "12px", border: "1px solid #f1d38b" }}
                        >
                            <Typography variant="body-bold" sx={{ color: "#694600", mb: 1.5 }}>
                                {t("settings.questionnaires.impact_of_change", "Impact of this change")}
                            </Typography>
                            <Stack gap={1}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="body" sx={{ color: "#694600" }}>
                                        {t("settings.questionnaires.posts_compared", "Posts compared")}:
                                    </Typography>
                                    <Typography variant="body-bold" sx={{ fontSize: 15, color: "#694600" }}>
                                        {comparisonTotalItems}
                                    </Typography>
                                </Stack>
                                <Divider sx={{ borderColor: "#f1d38b" }} />
                                {versionChangeItems.length > 0 && (
                                    <>
                                        <Box>
                                            <Typography variant="body-bold" sx={{ color: "#694600", mb: 0.75 }}>
                                                {t(
                                                    "settings.questionnaires.property_changes",
                                                    "Property changes between SSQ versions",
                                                )}
                                                :
                                            </Typography>
                                            <Stack gap={0.5} component="ul" sx={{ m: 0, pl: 2.25 }}>
                                                {versionChangeItems.map((change) => (
                                                    <Typography
                                                        key={change.key}
                                                        component="li"
                                                        variant="body"
                                                        sx={{ color: "#694600", "& strong": { fontWeight: 700 } }}
                                                        dangerouslySetInnerHTML={{ __html: change.content }}
                                                    />
                                                ))}
                                            </Stack>
                                        </Box>
                                        <Divider sx={{ borderColor: "#f1d38b" }} />
                                    </>
                                )}
                                <Typography variant="body" sx={{ color: "#694600" }}>
                                    {t(
                                        "settings.questionnaires.review_table_before_proceeding",
                                        "Please review the post migration details table before proceeding.",
                                    )}
                                </Typography>
                            </Stack>
                        </Box>

                        <Box mb={2}>
                            <Typography variant="body-bold" sx={{ mb: 1, display: "block" }}>
                                {t(
                                    "settings.questionnaires.post_migration_details",
                                    "Questionnaires migration details",
                                )}
                                :
                            </Typography>
                            <TableContainer
                                sx={{
                                    border: `1px solid ${borderColor}`,
                                    borderRadius: "8px",
                                    bgcolor: "white",
                                    maxHeight: 330,
                                }}
                            >
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow
                                            sx={{
                                                "& .MuiTableCell-root": {
                                                    bgcolor: "#fafaf9",
                                                    color: "text.secondary",
                                                    fontWeight: 700,
                                                    whiteSpace: "nowrap",
                                                },
                                            }}
                                        >
                                            <TableCell>{t("settings.questionnaires.entity_name", "Name")}</TableCell>
                                            <TableCell align="right">
                                                {t("settings.questionnaires.migrate", "Will be migrated")}
                                            </TableCell>
                                            <TableCell align="right">
                                                <FoodsTooltip
                                                    title={t(
                                                        "settings.questionnaires.transform_tooltip",
                                                        "Answers transformed using the migration rules defined on the target questionnaire properties",
                                                    )}
                                                    arrow
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "flex-end",
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        {t("settings.questionnaires.transform", "Will be transformed")}
                                                        <InfoOutlinedIcon
                                                            sx={{ fontSize: 16, color: "text.secondary" }}
                                                        />
                                                    </Box>
                                                </FoodsTooltip>
                                            </TableCell>
                                            <TableCell align="right">
                                                {t("settings.questionnaires.delete", "Will be deleted")}
                                            </TableCell>
                                            <TableCell align="right">
                                                <FoodsTooltip
                                                    title={t(
                                                        "settings.questionnaires.potentially_compatible_tooltip",
                                                        "Potentially compatible if data list option codes are compatible - calculated during migration for every post separately",
                                                    )}
                                                    arrow
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "flex-end",
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        {t(
                                                            "settings.questionnaires.potentially_compatible",
                                                            "Potentially compatible",
                                                        )}
                                                        <InfoOutlinedIcon
                                                            sx={{ fontSize: 16, color: "text.secondary" }}
                                                        />
                                                    </Box>
                                                </FoodsTooltip>
                                            </TableCell>
                                            <TableCell align="right">
                                                {t("settings.questionnaires.to_fill", "New to fill")}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {comparisonRows.map((comparison, index) => {
                                            const rowNumber = comparisonPage * comparisonRowsPerPage + index + 1;

                                            return (
                                                <TableRow
                                                    key={`${comparison.postId}-${rowNumber}`}
                                                    sx={{
                                                        "& .MuiTableCell-root": {
                                                            borderBottom: `1px solid ${borderColor}`,
                                                        },
                                                        "&:last-child .MuiTableCell-root": {
                                                            borderBottom: 0,
                                                        },
                                                    }}
                                                >
                                                    <TableCell
                                                        sx={
                                                            comparison.isUsedInActionPlan ?
                                                                {
                                                                    bgcolor: "rgba(211, 47, 47, 0.08)",
                                                                    borderLeft: "3px solid",
                                                                    borderLeftColor: "error.main",
                                                                }
                                                            :   undefined
                                                        }
                                                    >
                                                        <FoodsTooltip
                                                            title={
                                                                comparison.isUsedInActionPlan ?
                                                                    t(
                                                                        "settings.questionnaires.action_plan_post_tooltip",
                                                                        "Used in an action plan · Post ID: {{postId}}",
                                                                        { postId: comparison.postId },
                                                                    )
                                                                :   t(
                                                                        "settings.questionnaires.post_id_tooltip",
                                                                        "Post ID: {{postId}}",
                                                                        { postId: comparison.postId },
                                                                    )
                                                            }
                                                            arrow
                                                        >
                                                            <Typography
                                                                variant="body-bold"
                                                                sx={{
                                                                    display: "block",
                                                                    maxWidth: 340,
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                {comparison.entityName}
                                                            </Typography>
                                                        </FoodsTooltip>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: "#087a3b", fontWeight: 700 }}>
                                                        {comparison.migrate.toLocaleString()}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: "#00796b", fontWeight: 700 }}>
                                                        {comparison.transform.toLocaleString()}
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{ color: "error.main", fontWeight: 700 }}
                                                    >
                                                        {comparison.delete.toLocaleString()}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: "#5b5fc7", fontWeight: 700 }}>
                                                        {comparison.potentiallyCompatible.toLocaleString()}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: "#8a5a00", fontWeight: 700 }}>
                                                        {comparison.toFill.toLocaleString()}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        {isLoadingComparison && (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                    <CircularProgress size={20} />
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={comparisonTotalItems}
                                page={comparisonPage}
                                onPageChange={async (_, newPage) => {
                                    if (
                                        assignment?.campaign.id === undefined ||
                                        selectedVersionId === null ||
                                        currentVersionId === undefined
                                    )
                                        return;

                                    setComparisonPage(newPage);
                                    setConfirmed(false);
                                    await ssqPostVersionComparison.mutateAsync({
                                        campaignId: assignment.campaign.id,
                                        ssqVersionId: selectedVersionId,
                                        sourceSsqVersionId: currentVersionId,
                                        page: newPage + 1,
                                        itemsPerPage: comparisonRowsPerPage,
                                    });
                                }}
                                rowsPerPage={comparisonRowsPerPage}
                                rowsPerPageOptions={[5, 10, 20, 50]}
                                onRowsPerPageChange={async (event) => {
                                    if (
                                        assignment?.campaign.id === undefined ||
                                        selectedVersionId === null ||
                                        currentVersionId === undefined
                                    )
                                        return;

                                    const nextRowsPerPage = parseInt(event.target.value, 10);
                                    setComparisonRowsPerPage(nextRowsPerPage);
                                    setComparisonPage(0);
                                    setConfirmed(false);
                                    await ssqPostVersionComparison.mutateAsync({
                                        campaignId: assignment.campaign.id,
                                        ssqVersionId: selectedVersionId,
                                        sourceSsqVersionId: currentVersionId,
                                        page: 1,
                                        itemsPerPage: nextRowsPerPage,
                                    });
                                }}
                                labelRowsPerPage={t("component.table.rows_per_page", "Rows per page")}
                                disabled={isLoadingComparison}
                                sx={{
                                    borderLeft: `1px solid ${borderColor}`,
                                    borderRight: `1px solid ${borderColor}`,
                                    borderBottom: `1px solid ${borderColor}`,
                                    borderBottomLeftRadius: "8px",
                                    borderBottomRightRadius: "8px",
                                    bgcolor: "white",
                                }}
                            />
                        </Box>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={confirmed}
                                    onChange={(e) => setConfirmed(e.target.checked)}
                                    sx={{ color: borderColor, "&.Mui-checked": { color: "error.main" }, py: 0.5 }}
                                />
                            }
                            label={
                                <Stack gap={0.5}>
                                    <Typography variant="body">
                                        {t(
                                            "settings.questionnaires.confirm_migration_message",
                                            "I reviewed the post migration details table for campaign {{campaign}} and understand that applying this version may migrate, delete, or require new answers.",
                                            {
                                                campaign: assignment?.campaign.label,
                                            },
                                        )}
                                    </Typography>
                                    {hasRelatedActionPlans && (
                                        <Typography variant="body">
                                            {t(
                                                "settings.questionnaires.confirm_related_action_plans_stale_message",
                                                "I understand that {{actionPlanCount}} related action plans will be marked as stale and will require action on them.",
                                                {
                                                    actionPlanCount: relatedActionPlansCount,
                                                },
                                            )}
                                        </Typography>
                                    )}
                                </Stack>
                            }
                            sx={{ alignItems: "flex-start", mx: 0 }}
                        />
                    </DialogContent>

                    <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${borderColor}`, gap: 1 }}>
                        <Button
                            onClick={() => {
                                setStep("pick");
                                setConfirmed(false);
                            }}
                            disabled={isApplying}
                            sx={{ fontWeight: 600, color: "text.secondary", textTransform: "none" }}
                        >
                            {t("settings.questionnaires.back", "Back")}
                        </Button>
                        <Button
                            variant="contained"
                            disabled={!confirmed || isApplying}
                            onClick={() => {
                                if (confirmed && selectedVersionId) onApply(selectedVersionId);
                            }}
                            startIcon={isApplying ? <CircularProgress size={16} color="inherit" /> : undefined}
                            color="error"
                            sx={{
                                fontWeight: 700,
                                textTransform: "none",
                                borderRadius: "999px",
                                px: 3,
                                boxShadow: "none",
                                "&:hover": { boxShadow: "none" },
                            }}
                        >
                            {t("settings.questionnaires.apply_migration", "Apply version migration")}
                        </Button>
                    </DialogActions>
                </>
            }
        </Dialog>
    );
};
